// src/CartHandler.js
import shopifyFetch from "./APIFetch.js";

const CART_ID_KEY = "shopify_cart_id";

const getCartId = () => localStorage.getItem(CART_ID_KEY);
const setCartId  = (id) => localStorage.setItem(CART_ID_KEY, id);
const clearCartId = () => localStorage.removeItem(CART_ID_KEY);

// fire-and-forget UI update signal (App subscribes and bumps BasketContext version)
const emitCartUpdated = () => {
    try {
        window.dispatchEvent(new Event("cartUpdated"));
    } catch (_) {}
};

// utility: find the cart line for a given variant and return its node (or null)
const findLineByVariant = (cart, variantId) => {
    const edges = cart?.lines?.edges ?? [];
    for (const e of edges) {
        const merchId = e?.node?.merchandise?.id;
        if (merchId === variantId) return e?.node ?? null;
    }
    return null;
};

// --- fragments we reuse in queries ---
const LINES_FRAGMENT = `
  lines(first: 100) {
    edges {
      node {
        id
        quantity
        merchandise {
          ... on ProductVariant {
            id
            title
            price { amount currencyCode }
            product { title }
            image { url altText }
            selectedOptions { name value }
          }
        }
      }
    }
  }
`;

// helper: create a brand new cart with a line
async function createCartWithLine(variantId, quantity) {
    const query = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart {
          id
          checkoutUrl
          ${LINES_FRAGMENT}
        }
        userErrors { field message }
      }
    }
  `;
    const variables = { input: { lines: [{ quantity, merchandiseId: variantId }] } };
    const res = await shopifyFetch(query, variables);
    const payload = res?.cartCreate;
    const errs = payload?.userErrors ?? [];
    const newCart = payload?.cart ?? null;

    if (errs.length) return { __error: true, errors: errs };
    if (!newCart?.id) return { __error: true, errors: [{ message: "cartCreate returned no cart" }] };

    const line = findLineByVariant(newCart, variantId);
    if (!line || !line.quantity || line.quantity < 1) {
        return { __error: true, errors: [{ message: "Line not added or quantity < 1" }] };
    }

    setCartId(newCart.id);
    emitCartUpdated();
    return { cart: newCart, addedLine: line, addedQuantity: line.quantity };
}

// === Robust addToCart: handles stale cart IDs transparently and emits updates ===
export const addToCart = async (variantId, quantity = 1) => {
    console.trace("🧨 addToCart() was called");

    if (!variantId || quantity < 1) {
        return { __error: true, errors: [{ message: "Invalid variant or quantity" }] };
    }

    const existingCartId = getCartId();

    // No cart? Create one and add the line atomically.
    if (!existingCartId) {
        try {
            return await createCartWithLine(variantId, quantity);
        } catch (e) {
            return { __error: true, errors: [{ message: String(e?.message || e) }] };
        }
    }

    // We have a cartId – try adding to it.
    const query = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
          ${LINES_FRAGMENT}
        }
        userErrors { field message }
      }
    }
  `;
    const variables = {
        cartId: existingCartId,
        lines: [{ quantity, merchandiseId: variantId }]
    };

    try {
        const res = await shopifyFetch(query, variables);
        const payload = res?.cartLinesAdd;
        const errs = payload?.userErrors ?? [];
        const cart = payload?.cart ?? null;

        // If the cart vanished/expired, nuke the stale id and transparently recreate.
        const cartMissing = !cart?.id;
        const notFoundError = errs.some(e =>
            String(e?.message || "").toLowerCase().includes("not found")
            || String(e?.message || "").toLowerCase().includes("does not exist")
        );

        if (cartMissing || notFoundError) {
            clearCartId();
            try {
                return await createCartWithLine(variantId, quantity);
            } catch (e) {
                return { __error: true, errors: [{ message: String(e?.message || e) }] };
            }
        }

        if (errs.length) {
            return { __error: true, errors: errs };
        }

        const line = findLineByVariant(cart, variantId);
        if (!line || !line.quantity || line.quantity < 1) {
            return { __error: true, errors: [{ message: "Line not added or quantity < 1" }] };
        }

        emitCartUpdated();
        return { cart, addedLine: line, addedQuantity: line.quantity };
    } catch (e) {
        return { __error: true, errors: [{ message: String(e?.message || e) }] };
    }
};

export const getCart = async () => {
    const cartId = getCartId();
    if (!cartId) return null;

    const query = `
    query GetCart($cartId: ID!) {
      cart(id: $cartId) {
        id
        checkoutUrl
        ${LINES_FRAGMENT}
      }
    }
  `;

    const variables = { cartId };

    try {
        const res = await shopifyFetch(query, variables);
        const cart = res?.cart ?? null;

        // If Shopify says the cart doesn't exist anymore, clear the stale ID and report empty.
        if (!cart?.id) {
            clearCartId();
            return null;
        }

        const lines = cart?.lines?.edges || [];
        const items = lines.map(({ node }) => {
            const v = node?.merchandise; // ProductVariant
            const price = Number(v?.price?.amount ?? 0);
            const currency = v?.price?.currencyCode ?? "PLN";
            const imgUrl = v?.image?.url || null;
            const imgAlt = v?.image?.altText ?? v?.product?.title ?? v?.title ?? null;
            const options = (v?.selectedOptions || []).map(o => ({ name: o.name, value: o.value }));

            return {
                // legacy flat fields
                itemID: node.id,
                itemAmount: node.quantity,
                itemName: v?.product?.title ?? v?.title ?? "",
                itemPrice: price,
                itemCurrency: currency,
                itemImage: imgUrl,
                itemImageAlt: imgAlt,
                variantTitle: v?.title ?? "",
                variantID: v?.id,
                options,
                // shape expected by BasketItem.jsx
                quantity: node.quantity,
                item: {
                    id: node.id,
                    variantId: v?.id,
                    img: imgUrl,
                    alt: imgAlt,
                    name: v?.product?.title ?? v?.title ?? "",
                    price,
                    currency,
                    options,
                }
            };
        });

        return {
            cartId: cart.id,
            checkoutUrl: cart.checkoutUrl ?? null,
            items
        };
    } catch (err) {
        console.error("Failed to fetch cart:", err);
        return null;
    }
};

export const removeFromCart = async (cartLineId) => {
    const cartId = getCartId();
    if (!cartId) return null;

    const query = `
    mutation removeFromCart($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          id
          checkoutUrl
          ${LINES_FRAGMENT}
        }
        userErrors { field message }
      }
    }
  `;

    const variables = { cartId, lineIds: [cartLineId] };

    try {
        const res = await shopifyFetch(query, variables);
        const errs = res?.cartLinesRemove?.userErrors ?? [];
        if (errs.length) {
            // if cart disappeared, clear it so next add recreates
            const cart = res?.cartLinesRemove?.cart;
            if (!cart?.id) clearCartId();
            return { __error: true, errors: errs };
        }
        emitCartUpdated();
        return res.cartLinesRemove?.cart;
    } catch (err) {
        console.error("Failed to remove from cart:", err);
        return { __error: true, errors: [{ message: String(err?.message || err) }] };
    }
};

export const clearCart = async () => {
    const cartId = getCartId();
    if (!cartId) return null;

    const cart = await getCart();
    if (!cart?.items?.length) return;

    const lineIds = cart.items.map(item => item.itemID);

    const query = `
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          id
          checkoutUrl
          lines(first: 100) { edges { node { id } } }
        }
        userErrors { field message }
      }
    }
  `;

    const variables = { cartId, lineIds };

    try {
        const res = await shopifyFetch(query, variables);
        const errs = res?.cartLinesRemove?.userErrors ?? [];
        if (errs.length) {
            const cartRes = res?.cartLinesRemove?.cart;
            if (!cartRes?.id) clearCartId();
            return { __error: true, errors: errs };
        }
        emitCartUpdated();
        return res.cartLinesRemove?.cart;
    } catch (err) {
        console.error("Failed to clear cart:", err);
        return { __error: true, errors: [{ message: String(err?.message || err) }] };
    }
};

export const updateCartItemQuantity = async (itemID, newAmount) => {
    const cartId = getCartId();
    if (!cartId || !itemID || newAmount < 1) return null;

    const query = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
          ${LINES_FRAGMENT}
        }
        userErrors { field message }
      }
    }
  `;

    const variables = { cartId, lines: [{ id: itemID, quantity: newAmount }] };

    try {
        const res = await shopifyFetch(query, variables);
        const errs = res?.cartLinesUpdate?.userErrors ?? [];
        if (errs.length) {
            const cart = res?.cartLinesUpdate?.cart;
            if (!cart?.id) clearCartId();
            return { __error: true, errors: errs };
        }
        emitCartUpdated();
        return res.cartLinesUpdate?.cart;
    } catch (err) {
        console.error("Failed to update cart item quantity:", err);
        return { __error: true, errors: [{ message: String(err?.message || err) }] };
    }
};
